<template>
  <BaseModal
    modal-id="account-edit-modal"
    title="アカウント編集"
    v-model="internalVisible"
    size="lg"
    :center-content="false"
    header-style="gradient"
    no-close-on-backdrop
    no-close-on-esc
    @cancel="$emit('close')"
  >
    <!-- モーダルボディのコンテンツ -->
    <form @submit.prevent="handleSubmit">
      <div class="account-edit-form">
        <!-- ユーザーID（読み取り専用） -->
        <BaseReadOnlyField
          label="ユーザーID"
          :value="formData.userId"
        />

        <!-- 属性（読み取り専用） -->
        <BaseReadOnlyField
          label="属性"
          :value="formData.attribute"
        />

        <!-- 企業（読み取り専用） -->
        <BaseReadOnlyField
          label="企業"
          :value="formData.company"
        />

        <!-- パスワード（編集可能） -->
        <div class="password-input-container">
          <BaseInput
            v-model="formData.password"
            label="新しいパスワード"
            :type="showPassword ? 'text' : 'password'"
            placeholder="英数字を含む6文字以上（例: abc123）"
            :error-message="errors.password"
            description="• 英数字のみ使用可能 • 英語と数字の両方を含む • 6文字以上"
            required
            autocomplete="new-password"
            @update:model-value="handlePasswordInput"
            @enter="handleSubmit"
          />
          <button
            type="button"
            class="password-toggle-btn"
            @click="showPassword = !showPassword"
            :aria-label="showPassword ? 'パスワードを隠す' : 'パスワードを表示'"
          >
            <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            {{ showPassword ? '隠す' : '表示' }}
          </button>
        </div>
      </div>
    </form>

    <!-- カスタムフッター -->
    <template #footer>
      <div class="modal-actions">
        <BaseButton
          variant="secondary"
          @click="$emit('close')"
        >
          キャンセル
        </BaseButton>
        <BaseButton
          variant="primary"
          @click="handleSubmit"
          :disabled="!isFormValid || internalLoading"
        >
          {{ internalLoading ? '更新中...' : '更新' }}
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
/**
 * AccountEditModal - アカウント編集モーダル (Composition API)
 *
 * Composition APIを使用した現代的な実装。
 */
import { ref, reactive, computed, watch } from 'vue'
import BaseModal from '@/components/molecules/BaseModal.vue'
import BaseInput from '@/components/atoms/BaseInput.vue'
import BaseButton from '@/components/atoms/BaseButton.vue'
import BaseReadOnlyField from '@/components/atoms/BaseReadOnlyField.vue'
import ContentText from '@/components/atoms/ContentText.vue'

const props = defineProps({
  /**
   * モーダルの表示状態
   */
  visible: {
    type: Boolean,
    default: false
  },
  /**
   * 編集対象のアカウントデータ
   */
  account: {
    type: Object,
    default: () => ({})
  },
  /**
   * 送信中の状態
   */
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'save'])

// パスワード表示切替フラグ
const showPassword = ref(false)

// フォームデータ
const formData = reactive({
  userId: '',
  attribute: '',
  company: '',
  password: ''
})

// エラー状態
const errors = reactive({})

// 内部ローディング状態
const internalLoading = ref(false)

/**
 * フォームのバリデーション状態
 */
const isFormValid = computed(() => {
  if (!formData.password) return false
  if (formData.password.length < 6) return false
  if (!/^[a-zA-Z0-9]+$/.test(formData.password)) return false
  if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(formData.password)) return false
  return true
})

/**
 * BaseModalのv-model用の内部状態
 */
const internalVisible = computed({
  get() {
    return props.visible
  },
  set(value) {
    if (!value) {
      emit('close')
    }
  }
})

/**
 * フォームデータを初期化
 */
const initializeForm = (account) => {
  // 同じアカウントの場合は初期化をスキップ（パスワード入力を保持）
  if (formData.userId === account.userId && formData.attribute === account.attribute) {
    return
  }

  formData.userId = account.userId || ''
  formData.attribute = account.attribute || ''
  formData.company = account.company || ''
  formData.password = ''
  Object.keys(errors).forEach(key => delete errors[key])
}

/**
 * バリデーションを実行
 */
const validateForm = () => {
  Object.keys(errors).forEach(key => delete errors[key])

  // パスワード必須チェック
  if (!formData.password) {
    errors.password = 'パスワードは必須です'
  } else if (formData.password.length < 6) {
    errors.password = 'パスワードは6文字以上で入力してください'
  } else if (!/^[a-zA-Z0-9]+$/.test(formData.password)) {
    errors.password = 'パスワードは英数字のみで入力してください'
  } else if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(formData.password)) {
    errors.password = 'パスワードは英語と数字の両方を含む必要があります'
  }

  return Object.keys(errors).length === 0
}

/**
 * エラーをクリア
 */
const clearError = (field) => {
  if (errors[field]) {
    delete errors[field]
  }
}

/**
 * パスワード入力時の処理
 */
const handlePasswordInput = () => {
  // 入力中はエラーをクリア
  clearError('password')
}

/**
 * フォーム送信処理
 */
const handleSubmit = async () => {
  if (!validateForm()) {
    return // バリデーション失敗時は何もしない
  }

  internalLoading.value = true

  try {
    const updateData = {
      id: props.account.id,
      userId: formData.userId,
      attribute: formData.attribute,
      company: formData.company,
      password: formData.password
    }

    emit('save', updateData)

    // 成功時は親コンポーネントがモーダルを閉じるので、
    // そのタイミングでローディング状態をリセット
  } catch (error) {
    // エラー時はローディング状態をリセット
    internalLoading.value = false
  }
}

// アカウントデータが変更されたときにフォームを初期化
watch(() => props.account, (newAccount) => {
  if (newAccount && Object.keys(newAccount).length > 0) {
    initializeForm(newAccount)
  }
}, { immediate: true })

// モーダルが表示されたときにフォームを初期化
watch(() => props.visible, (newVisible) => {
  if (newVisible && props.account) {
    initializeForm(props.account)
  } else if (!newVisible) {
    // モーダルが閉じられたときにローディング状態をリセット
    internalLoading.value = false
  }
})
</script>

<style lang="scss" scoped>
// アカウント編集フォームのスタイル
.account-edit-form {
  padding: 0;
}

.required {
  color: #ef4444;
  font-weight: 700;
}

.readonly-input {
  background-color: #f9fafb;
  border-color: #e5e7eb;
  color: #6b7280;
  cursor: not-allowed;
}

.error {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.error-message {
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  font-weight: 500;
}

.password-help {
  margin-top: 0.5rem;

  small {
    color: #6b7280;
    font-size: 0.75rem;
    line-height: 1.4;
  }
}

.password-input-container {
  position: relative;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 2.25rem;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: color 0.2s;
  z-index: 10;

  &:hover {
    color: #3b82f6;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 0.25rem;
  }

  i {
    font-size: 1rem;
  }
}

// モーダルアクションのスタイル
.modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

// レスポンシブ対応
@media (max-width: 768px) {
  .modal-actions {
    flex-direction: column-reverse;
    gap: 0.5rem;

    > * {
      width: 100%;
    }
  }
}
</style>
